/**
 * useSupervisorNotesPoller — polls the backend for pending supervisor notes
 * targeted at the current apprentice license, pushes them into the game's
 * Messagerie via `pushCustomMessage`, and acks them so they don't re-trigger.
 *
 * Polling cadence:
 *   - Once on mount (so a note posted while the player was offline lands
 *     immediately when they open the game).
 *   - Every monthly close (year/month tag changes).
 *   - 10-minute fallback timer for long sessions.
 *
 * Silent on failure (offline-first design).
 */
import { useEffect, useRef } from 'react';
import useGameStore from '../store/gameStore';
import useLicenseStore from '../store/licenseStore';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

const TONE_TO_MESSAGE_TONE = {
  encouragement: 'friendly',
  advice:        'formal',
  warning:       'urgent',
};

const TONE_TO_SUBJECT_PREFIX = {
  encouragement: 'Encouragement',
  advice:        'Conseil',
  warning:       'Avertissement',
};

async function fetchPendingNotes(licenseKey) {
  if (!licenseKey || !BACKEND) return [];
  try {
    const resp = await fetch(
      `${BACKEND}/api/master/notes/by-apprentice?license_key=${encodeURIComponent(licenseKey)}`
    );
    if (!resp.ok) return [];
    const data = await resp.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch (_) {
    return [];
  }
}

async function ackNote(noteId, licenseKey) {
  if (!noteId || !BACKEND) return;
  try {
    await fetch(`${BACKEND}/api/master/notes/${encodeURIComponent(noteId)}/ack?license_key=${encodeURIComponent(licenseKey)}`, {
      method: 'POST',
      keepalive: true,
    });
  } catch (_) { /* silent */ }
}

async function drainNotes(licenseKey) {
  const state = useGameStore.getState();
  if (!state.gameStarted || !state.pushCustomMessage) return;
  const notes = await fetchPendingNotes(licenseKey);
  if (!notes.length) return;
  for (const n of notes) {
    const tone = TONE_TO_MESSAGE_TONE[n.tone] || 'formal';
    const subjectPrefix = TONE_TO_SUBJECT_PREFIX[n.tone] || 'Message';
    state.pushCustomMessage({
      senderType: 'supervisor',
      senderName: n.master_holder || 'Responsable pédagogique',
      senderId: n.master_id || null,
      tone,
      kind: 'info',
      subject: `${subjectPrefix} de votre Responsable`,
      body: n.body || '',
      ttlDays: 14,
      payload: { supervisorNoteId: n.id, supervisorTone: n.tone },
    });
    await ackNote(n.id, licenseKey);
  }
}

export default function useSupervisorNotesPoller() {
  const licenseKey = useLicenseStore(s => s.licenseKey);
  const monthRef = useRef(null);
  const startedRef = useRef(false);

  // Effect 1: initial drain + month-change detection.
  useEffect(() => {
    if (!licenseKey) return undefined;
    // Initial drain when the hook mounts and game is started.
    const initial = useGameStore.getState();
    if (initial.gameStarted && !startedRef.current) {
      startedRef.current = true;
      monthRef.current = `${initial.year}-${initial.month}`;
      drainNotes(licenseKey);
    }
    const unsubscribe = useGameStore.subscribe((state) => {
      const tag = `${state.year}-${state.month}`;
      if (state.gameStarted && !startedRef.current) {
        startedRef.current = true;
        monthRef.current = tag;
        drainNotes(licenseKey);
        return;
      }
      if (state.gameStarted && monthRef.current && tag !== monthRef.current) {
        monthRef.current = tag;
        drainNotes(licenseKey);
      }
    });
    return unsubscribe;
  }, [licenseKey]);

  // Effect 2: 10-minute fallback for long sessions where no monthly close fires.
  useEffect(() => {
    if (!licenseKey) return undefined;
    const id = setInterval(() => {
      const state = useGameStore.getState();
      if (state.gameStarted) drainNotes(licenseKey);
    }, FALLBACK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [licenseKey]);
}
